/**
 * Turns a `CalendarView` + anchor `LocalDate` into the concrete period the
 * user is looking at: a stable cache key (`getRangeKey`), a `‹ ›`-navigable
 * step (`stepAnchor`), and a human label for the header (`getPeriodLabel`).
 * Built on top of the pure primitives in `calendarDate.ts`; still no React.
 */
import { format } from 'date-fns';
import type { CalendarView } from '../../../types/calendar';
import {
  addDays,
  addMonths,
  getDayRange,
  getMonthGridRange,
  getWeekRange,
  type LocalDate,
  localDateKey,
  toNativeDate,
} from './calendarDate';

const WEEK_STARTS_ON = 0 as const; // Sunday — no per-user setting yet.

/**
 * How far forward the agenda looks. Long enough to be a real pipeline view,
 * short enough that one fetch covers it.
 */
export const AGENDA_RANGE_DAYS = 30;

/**
 * Makes every switch below exhaustive: adding a member to CalendarView now
 * fails to compile here instead of silently rendering as Day, which is exactly
 * what happened while each of these functions ended in an unguarded fallthrough.
 */
function assertNever(view: never): never {
  throw new Error(`Unhandled calendar view: ${String(view)}`);
}

function agendaDays(anchor: LocalDate): LocalDate[] {
  return Array.from({ length: AGENDA_RANGE_DAYS }, (_, offset) => addDays(anchor, offset));
}

/**
 * A cache key scoped to the *period* being viewed, not the exact anchor day
 * within it — e.g. any day in August 2026 keys the month view identically,
 * so `useCalendarEvents` doesn't churn its cache as the user pages within a
 * view without actually changing the visible period. See `useCalendarEvents`
 * for why this only affects caching and not what gets fetched today.
 */
export function getRangeKey(view: CalendarView, anchor: LocalDate): string {
  switch (view) {
    case 'month':
      return `month:${anchor.year}-${String(anchor.month).padStart(2, '0')}`;
    case 'week': {
      const [weekStart] = getWeekRange(anchor, WEEK_STARTS_ON);
      return `week:${localDateKey(weekStart)}`;
    }
    case 'day':
      return `day:${localDateKey(anchor)}`;
    case 'agenda':
      return `agenda:${localDateKey(anchor)}`;
    default:
      return assertNever(view);
  }
}

export interface CalendarEventsDateRange {
  /** Range start, ISO 8601 UTC instant (inclusive). */
  from: string;
  /** Range end, ISO 8601 UTC instant (exclusive). */
  to: string;
}

/**
 * The concrete [from, to) instant range the visible period covers, as ISO
 * 8601 UTC strings — the shape `GET /calendar/events`'s `from`/`to` query
 * params expect (backend overlap predicate: `startsAt < to AND endsAt >
 * from`, see calendar.service.ts's `listEvents`).
 *
 * Built from the same day lists actually rendered on screen
 * (`getMonthGridRange`/`getWeekRange`/`getDayRange`) rather than recomputed,
 * so the fetched window always matches exactly what's drawn — including the
 * month grid's leading/trailing days from adjacent months, which the grid
 * renders and therefore must fetch too.
 *
 * `to` is local midnight the day AFTER the last visible day (exclusive), so
 * an event landing anywhere within that last day is still included. No
 * further padding is needed: this is the same half-open, midnight-aligned
 * day boundary `eventTouchesLocalDay` (calendarEventBucketing.ts) already
 * uses to decide which days an event touches, so the fetched window lines
 * up exactly with what the client buckets — nothing fetched goes unbucketed,
 * nothing bucketed goes unfetched.
 */
export function getViewDateRange(view: CalendarView, anchor: LocalDate): CalendarEventsDateRange {
  const days = getVisibleDays(view, anchor);

  const firstDay = days[0];
  const lastDay = days[days.length - 1];

  return {
    from: toNativeDate(firstDay).toISOString(),
    to: toNativeDate(addDays(lastDay, 1)).toISOString(),
  };
}

/** The concrete local days a view puts on screen. */
function getVisibleDays(view: CalendarView, anchor: LocalDate): LocalDate[] {
  switch (view) {
    case 'month':
      return getMonthGridRange(anchor.year, anchor.month, WEEK_STARTS_ON).flat();
    case 'week':
      return getWeekRange(anchor, WEEK_STARTS_ON);
    case 'day':
      return getDayRange(anchor);
    case 'agenda':
      return agendaDays(anchor);
    default:
      return assertNever(view);
  }
}

/** Moves the anchor one period forward (`direction: 1`) or back (`-1`). */
export function stepAnchor(view: CalendarView, anchor: LocalDate, direction: 1 | -1): LocalDate {
  switch (view) {
    case 'month':
      return addMonths(anchor, direction);
    case 'week':
      return addDays(anchor, 7 * direction);
    case 'day':
      return addDays(anchor, direction);
    case 'agenda':
      return addDays(anchor, AGENDA_RANGE_DAYS * direction);
    default:
      return assertNever(view);
  }
}

function formatSpanLabel(start: LocalDate, end: LocalDate): string {
  const sameMonth = start.month === end.month && start.year === end.year;
  const startLabel = format(toNativeDate(start), 'MMM d');
  const endLabel = format(toNativeDate(end), sameMonth ? 'd, yyyy' : 'MMM d, yyyy');
  return `${startLabel} – ${endLabel}`;
}

export function getPeriodLabel(view: CalendarView, anchor: LocalDate): string {
  switch (view) {
    case 'day':
      return format(toNativeDate(anchor), 'EEEE, MMMM d, yyyy');
    case 'week': {
      const [weekStart, , , , , , weekEnd] = getWeekRange(anchor, WEEK_STARTS_ON);
      return formatSpanLabel(weekStart, weekEnd);
    }
    case 'month':
      return format(toNativeDate(anchor), 'MMMM yyyy');
    case 'agenda':
      return formatSpanLabel(anchor, addDays(anchor, AGENDA_RANGE_DAYS - 1));
    default:
      return assertNever(view);
  }
}
